import SceneObject from "../../../lib/services/scene/sceneobject";
import CharacterBuilder from "../../../lib/services/render/builders/character_builder";
import { CharacterRuntime } from "../../../lib/services/render/builders/character_runtime";
import CharacterBuilderGpu from "../../../lib/services/compute/character_builder_gpu";
import AnimationPool from "../../../lib/services/compute/animation_pool";
import AnimationLibraryPool from "../../../lib/services/compute/animation_library_pool";
import AnimationComputePipeline from "../../../lib/services/compute/pipelines/animation_compute_pipeline";
import AnimationControllerGpu from "../../../lib/services/animation/animation_controller_gpu";
import Camera from "../../../lib/services/scene/camera";
import ShaderService from "../../../lib/services/shader/shader_service";
import ResourceService from "../../../lib/services/resource/resourceservice";
import BlobShadow from "../../../lib/services/scene/blob_shadow";
import SkinnedOutlineRenderPipeline from "../../../lib/services/render/pipelines/skinned_outline_renderpipeline";
import JointAttachment from "../../../lib/services/animation/joint_attachment";
import { identityMat4, trsToMat4 } from "../../../lib/services/animation/anim_math";

export type CharacterJointOffset = {
    translation?: readonly [number, number, number];
    rotation?: readonly [number, number, number, number];
    scale?: readonly [number, number, number];
};

export default class Character extends SceneObject
{

     shader_name: string;
     mesh_name: string;
     texture_name: string;
     animation_name: string;
     default_loop_index: number;

     builder: CharacterBuilder;
     runtime: CharacterRuntime | null = null;
    // GPU animation infrastructure.
     builder_gpu: CharacterBuilderGpu | null = null;
     library_pool: AnimationLibraryPool | null = null;
    animation_pool: AnimationPool | null = null;
     animation_compute_pipeline: AnimationComputePipeline | null = null;
     animation_controller_gpu: AnimationControllerGpu | null = null;
    //cheap shadow
    shadow: BlobShadow;

    // Opt-in inverted-hull outline pass (see SkinnedOutlineRenderPipeline
    // doc comment). Off by default for every Character - there is no
    // engine-wide or Character default outline. A subclass (e.g. a single
    // demo character) can call enable_outline() after construction to
    // turn it on for just that instance; every other Character is
    // completely unaffected.
    outline_renderpipeline: SkinnedOutlineRenderPipeline;
    private outline_enabled: boolean = false;
    private readonly managed_joint_attachments = new Map<SceneObject, JointAttachment>();
    private joint_attachment_serial = 0;

    constructor(key:string,mesh_name:string,texture_name:string,animation_name:string,shader_name:string ,default_loop_index:number)
    {
        super(key)

         this.mesh_name = mesh_name;
         this.shader_name= shader_name;
         this.texture_name= texture_name;
         this.animation_name = animation_name;
         this.default_loop_index = default_loop_index;
         this.builder = new CharacterBuilder();
         this.shadow = new BlobShadow(key+"_shadow", 1);
         this.add_child(this.shadow);
         this.outline_renderpipeline = new SkinnedOutlineRenderPipeline(key, "outline_skinned");
    }

    /** Opt-in toggle for the demo outline pass. Off by default. */
    enable_outline(enabled: boolean = true): void
    {
        this.outline_enabled = enabled;
    }

    set_outline_line_width(width: number): void
    {
        this.outline_renderpipeline.set_line_width(width);
    }

    set_outline_line_color(r: number, g: number, b: number, a: number): void
    {
        this.outline_renderpipeline.set_line_color(r, g, b, a);
    }

     override async build(
        device: GPUDevice,
        format: GPUTextureFormat,
        shader_service: ShaderService,
        resource_service: ResourceService
      ): Promise<void> 
      {
        this.runtime = await this.builder.build(
          {
            key: this.key,
            shader_name: this.shader_name,
            mesh_name: this.mesh_name,
            texture_name: this.texture_name,
            animation_name: this.animation_name,
            default_loop_index: this.default_loop_index,
          },
          device,
          format,
          shader_service,
          resource_service
        );

        // Reuses runtime.skinned_renderpipeline's already-built Mesh/GPU
        // buffers - no second glTF load or vertex/index re-upload. Built
        // unconditionally (cheap: no texture, small uniforms) so
        // enable_outline() can be toggled at any time afterwards without
        // re-running build(); draw()/prepare() below still gate all GPU
        // work on outline_enabled, so this has no effect unless a caller
        // opts in.
        this.outline_renderpipeline.build_from_mesh(
          device,
          format,
          shader_service,
          this.runtime.skinned_renderpipeline.mesh
        );
    
        //animation runs on gpu for performance 
         await this.build_gpu_animation(device, shader_service);
         await super.build(device, format, shader_service, resource_service);
    
        this.shadow.update_shadow(
          this.transform.position[0],
          this.transform.position[1],
          this.transform.position[2]
        );
    
        //initial animation
        if(this.animation_controller_gpu)
        {
            this.animation_controller_gpu.playLoopByIndex(
            this.default_loop_index); 
         }
    
       
      }
      
      log_animations():void
      {
          if(!this.runtime) return;
          console.log("animations for "+this.key);
          this.runtime.animation_library.log(); 
      }

      log_joints():void
      {
          if(!this.runtime) return;

          const model = this.runtime.model;
          model.skins.forEach((skin, skin_index) =>
          {
              const joints = skin.joints.map((node_index, joint_index) => ({
                  joint_index,
                  node_index,
                  name: model.nodes[node_index]?.name ?? "<unnamed>",
              }));

              console.groupCollapsed(
                  `joints for ${this.key}, skin ${skin_index} (${skin.name ?? "unnamed"})`
              );
              console.table(joints);
              console.groupEnd();
          });
      }

      attach_scene_object_to_joint(
          child: SceneObject,
          joint_name: string,
          offset: CharacterJointOffset | Float32Array = identityMat4(),
          attachment_name?: string
      ): JointAttachment
      {
          if(!this.runtime)
          {
              throw new Error(`character ${this.key} must be built before attaching to a joint`);
          }

          const model = this.runtime.model;
          const primitive = model.meshes[0];
          if(!primitive)
          {
              throw new Error(`character ${this.key} contains no mesh primitives`);
          }

          const skin_index = primitive.skinIndex;
          if(skin_index === undefined)
          {
              throw new Error(`character ${this.key}: primitive is not associated with a skin`);
          }

          const skin = model.skins[skin_index];
          const requested_name = joint_name.toLocaleLowerCase();
          let joint_index = skin.joints.findIndex((node_index) =>
              model.nodes[node_index]?.name === joint_name
          );
          if(joint_index < 0)
          {
              joint_index = skin.joints.findIndex((node_index) =>
                  model.nodes[node_index]?.name?.toLocaleLowerCase() === requested_name
              );
          }
          if(joint_index < 0)
          {
              throw new Error(`character ${this.key}: joint ${joint_name} was not found`);
          }

          this.detach_scene_object_from_joint(child);

          const local_matrix = offset instanceof Float32Array
              ? offset
              : trsToMat4(
                  new Float32Array(offset.translation ?? [0, 0, 0]),
                  new Float32Array(offset.rotation ?? [0, 0, 0, 1]),
                  new Float32Array(offset.scale ?? [1, 1, 1])
              );
          const resolved_attachment_name = attachment_name ??
              `${this.key}:${joint_name}:${child.key}:${this.joint_attachment_serial++}`;
          const attachment = new JointAttachment(
              resolved_attachment_name,
              joint_index,
              local_matrix
          );
          attachment.bind(model, skin_index, primitive.nodeIndex);

          this.add_attachment(attachment);
          if(!this.children.includes(child))
          {
              this.add_child(child);
          }
          this.attach_child(child, attachment.name);
          this.managed_joint_attachments.set(child, attachment);
          this.update_attachments();
          return attachment;
      }

      detach_scene_object_from_joint(
          child: SceneObject,
          remove_child: boolean = false
      ): boolean
      {
          const attachment = this.managed_joint_attachments.get(child);
          if(!attachment) return false;

          this.detach_child(child);
          const attachment_index = this.attachments.indexOf(attachment);
          if(attachment_index >= 0)
          {
              this.attachments.splice(attachment_index, 1);
          }
          this.managed_joint_attachments.delete(child);

          if(remove_child)
          {
              this.remove_child(child);
          }
          return true;
      }

      detach_all_scene_objects_from_joints(remove_children: boolean = false): void
      {
          for(const child of Array.from(this.managed_joint_attachments.keys()))
          {
              this.detach_scene_object_from_joint(child, remove_children);
          }
      }

      get_scene_object_joint_attachment(child: SceneObject): JointAttachment | null
      {
          return this.managed_joint_attachments.get(child) ?? null;
      }

      private async build_gpu_animation(
          device: GPUDevice,
          shader_service: ShaderService
        ): Promise<void> {
          if (!this.runtime) return;
      
          const model = this.runtime.model;
      
          if (!model.meshes.length) {
            throw new Error("character model "+this.key+ " contains no mesh primitives");
          }
      
          const primitive = model.meshes[0];
          const skin_index = primitive.skinIndex;
          if (skin_index === undefined) {
            throw new Error("character model "+this.key+ ": primitive is not associated with a skin");
          }
      
          const mesh_node_index = primitive.nodeIndex;
          this.builder_gpu = new CharacterBuilderGpu(this.key);
          await this.builder_gpu.build(
            device,
            model,
            this.runtime.animation_library,
            skin_index,
            mesh_node_index
          );
      
          this.library_pool = new AnimationLibraryPool();
          this.library_pool.initialize();
          this.library_pool.populate(this.builder_gpu);
      
          this.animation_pool = new AnimationPool();
          this.animation_pool.initialize(
            device,
            this.key,
            this.builder_gpu.node_count,
            this.builder_gpu.joint_count
          );
      
           this.animation_compute_pipeline = new AnimationComputePipeline(this.key);
           await this.animation_compute_pipeline.build(device, shader_service);
      
           this.animation_controller_gpu = new AnimationControllerGpu(
            this.key,
            this.runtime.animation_library,
            this.builder_gpu,
            this.library_pool,
            this.animation_pool,
            this.animation_compute_pipeline
          );
      
          //todo show how to attach to a bone
      
       }

       set_shadow_scale(scale:number):void
       {
        
         this.shadow.scale = scale;
         this.shadow.transform.set_scale(scale,scale,scale);
         this.shadow.update_shadow(
            this.transform.position[0],
            this.transform.position[1],
            this.transform.position[2]
                );

       }

       override  tick(delta_time: number): void 
       {
         if (!this.runtime) return;

            //animation controller needs to be ticked
            if (this.animation_controller_gpu)
            {
                this.animation_controller_gpu.tick(delta_time);
            }

            //update the matrix
            this.transform.update_matrix();

            // Joint attachments sample the current animation state and update
            // every bound SceneObject before child ticks run.
            this.update_attachments();

            //update the shadow
            this.shadow.update_shadow(
            this.transform.position[0],
            this.transform.position[1],
            this.transform.position[2]
                );

            super.tick(delta_time);
       }

       //compute function is called by the RenderService during compute pass
    override compute(device: GPUDevice, pass: GPUComputePassEncoder, encoder:GPUCommandEncoder): void
    {
        //need to encode the animation controllers pipeline
        if (this.animation_controller_gpu) {
        this.animation_controller_gpu.encode_compute(device, pass);
        }

        super.compute(device, pass,encoder);
    }

     //prepare is called before draw to update any matrices or gpu buffers
        override prepare(device: GPUDevice, camera: Camera): void {
        if (!this.runtime) return;
    
        if (this.animation_controller_gpu) {
          this.runtime.skinned_renderpipeline.prepare(
            device,
            camera,
            this.transform,
            this.animation_controller_gpu
          );

          // Must run after the base pipeline's prepare() above so it can
          // read this frame's already-acquired joint-palette lease
          // (get_current_joint_palette()) instead of acquiring a second,
          // independent lease.
          if (this.outline_enabled) {
            this.outline_renderpipeline.prepare(
              device,
              camera,
              this.transform,
              this.runtime.skinned_renderpipeline
            );
          }
        }
    
        super.prepare(device, camera);
      }


  //draw is called by RenderService for us to encode drawing commands for this object
  override draw(pass: GPURenderPassEncoder): void {
    // Base mesh first, outline second, same render pass - see
    // SkinnedOutlineRenderPipeline doc comment for why draw order plus
    // depthCompare "less" together keep the outline correctly occluded.
    this.runtime?.skinned_renderpipeline.draw(pass);
    if (this.outline_enabled) {
      this.outline_renderpipeline.draw(pass);
    }
    super.draw(pass);
  }

  //called after draw for any cleanup or needing to know the render has been submitted
  override finish_draw(): void 
  {
    // Outline pipeline's finish_draw() does not release the joint-palette
    // lease (it never acquired one) - only the base pipeline releases it,
    // exactly once, after both consumers' draw() calls above have run.
    if (this.outline_enabled) {
      this.outline_renderpipeline.finish_draw();
    }
    this.runtime?.skinned_renderpipeline.finish_draw();
    super.finish_draw();
  }

   ui_message(cmd: string, msg: string): void {
    super.ui_message(cmd, msg);
  }


}
